import { Alert, Box, Button, Chip, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha } from '@mui/material'
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined'
import DownloadOutlined from '@mui/icons-material/DownloadOutlined'
import { FunctionComponent, useContext, useRef, useState } from 'react'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { LoadingSpinner } from '../../presentation-components/display-elements-components/loading-spinner'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import GoodCategoryService from '../../../shared/services/good-category/good-category.service'
import { IGoodCategoryImportPreview, IGoodCategoryImportRow } from '../../../shared/services/good-category/good-category.model'
import { downloadExcel } from '../../../shared/utils/import-utils'

const ACCEPTED_FILE_TYPES = '.xlsx,.xls,.csv'

const changeLabel = (row: IGoodCategoryImportRow): string => {
  if (row.action === 'update') return 'Update reference(s)'
  if (row.action === 'unchanged') return 'Already exists'
  return 'Add'
}

const referenceLabel = (reference: string | null): string => reference ? reference.split(',').join(', ') : ''

/**
 * Spreadsheet import for the Product Categories settings page. Same shape as the nominal code
 * import dialog: download a template, choose a file, review every row with its own errors, then
 * save the rows that are ready.
 */
export const GoodCategoryImportDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  onImported: () => void,
}> = ({ open, closeHandler, onImported }) => {
  //Services
  const goodCategoryService = new GoodCategoryService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  //State
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<IGoodCategoryImportPreview | null>(null)
  const [fileErrorMessage, setFileErrorMessage] = useState<string>('')
  //Loading State
  const [isParsing, setIsParsing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  //Refs
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = () => {
    setFile(null)
    setPreview(null)
    setFileErrorMessage('')
    setIsParsing(false)
    setIsSubmitting(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    resetState()
    closeHandler()
  }

  const downloadTemplateHandler = async () => {
    try {
      setIsDownloading(true)
      const response = await goodCategoryService.getImportTemplate()
      downloadExcel(response, 'Product Categories')
    } catch (err) {
      userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      console.log(err)
    } finally {
      setIsDownloading(false)
    }
  }

  const fileChangeHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    if (!selected) return
    setFile(selected)
    setPreview(null)
    setFileErrorMessage('')
    try {
      setIsParsing(true)
      const response = await goodCategoryService.previewImport(selected)
      setPreview(response)
    } catch (err: any) {
      // The backend writes these messages for the user (no column found, wrong file type, too many
      // rows), so show them rather than a generic failure.
      setFileErrorMessage(err?.response?.data?.message || 'That file could not be read. Please check it is a valid .xlsx or .csv file.')
    } finally {
      setIsParsing(false)
    }
  }

  const submitHandler = async () => {
    if (!preview) return
    const rowsToSave = preview.rows.filter(row => row.isValid && row.action !== 'unchanged')
    if (rowsToSave.length === 0) return
    try {
      setIsSubmitting(true)
      const result = await goodCategoryService.importRows(rowsToSave.map(row => ({
        rowNumber: row.rowNumber,
        category: row.category,
        subCategoryOne: row.subCategoryOne,
        subCategoryTwo: row.subCategoryTwo,
        goodCategoryReference: row.goodCategoryReference,
      })))
      const parts: string[] = []
      if (result.created > 0) parts.push(`${result.created} ${result.created === 1 ? 'category' : 'categories'} added.`)
      if (result.updated > 0) parts.push(`${result.updated} ${result.updated === 1 ? 'category' : 'categories'} updated.`)
      const failed = result.failed?.length ?? 0
      if (failed > 0) parts.push(`${failed} ${failed === 1 ? 'row' : 'rows'} could not be saved.`)
      if (parts.length === 0) parts.push('No changes were needed.')
      userNotificationsContext.openSnackbar({
        open: true,
        message: parts.join(' '),
        ...(failed > 0 ? { backgroundColor: 'text.primary' } : {}),
      })
      onImported()
      handleClose()
    } catch (err: any) {
      userNotificationsContext.openSnackbar({ open: true, message: err?.response?.data?.message || 'There was an error, please try again', backgroundColor: 'text.primary' })
      console.log(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const summary = preview?.summary
  const canSubmit = Boolean(summary && summary.valid > 0)
  const submitButtonText = summary && summary.valid > 0
    ? `Save ${summary.valid} ${summary.valid === 1 ? 'category' : 'categories'}`
    : 'Save'

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title='Import Product Categories'
      maxWidth='lg'
      cancelHandler={handleClose}
      submitHandler={submitHandler}
      isSubmitting={isSubmitting}
      disableSaveButton={!canSubmit}
      submitButtonText={submitButtonText}
      submitButtonLoadingText='Saving'
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant='body2' color='text.secondary'>
            Add your product categories in bulk from a spreadsheet. Each row is one category, with up to two
            levels of subcategory and any product codes or references it covers. A category that already exists
            has its reference(s) updated; one that does not is created.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Button
            variant='outlined'
            onClick={downloadTemplateHandler}
            disabled={isDownloading}
            startIcon={<DownloadOutlined fontSize='small' />}
          >
            <Typography sx={{ pl: 0.5 }}>{isDownloading ? 'Preparing' : 'Download template'}</Typography>
          </Button>
          <Button
            variant='contained'
            onClick={() => fileInputRef.current?.click()}
            disabled={isParsing}
            startIcon={<FileUploadOutlined fontSize='small' />}
          >
            <Typography sx={{ pl: 0.5 }}>{file ? 'Choose a different file' : 'Choose file'}</Typography>
          </Button>
          {file && (
            <Typography variant='body2' color='text.secondary' sx={{ alignSelf: 'center' }}>
              {file.name}
            </Typography>
          )}
          <input
            ref={fileInputRef}
            type='file'
            accept={ACCEPTED_FILE_TYPES}
            onChange={fileChangeHandler}
            style={{ display: 'none' }}
          />
        </Stack>

        {fileErrorMessage && (
          <Alert severity='error'>{fileErrorMessage}</Alert>
        )}

        {isParsing && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <LoadingSpinner />
          </Box>
        )}

        {!isParsing && preview && summary && (
          <Stack spacing={2}>
            <Stack direction='row' spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Chip label={`${summary.total} ${summary.total === 1 ? 'row' : 'rows'} read`} size='small' variant='outlined' />
              <Chip label={`${summary.toCreate} to add`} size='small' variant='outlined' />
              {summary.toUpdate > 0 && (
                <Chip label={`${summary.toUpdate} to update`} size='small' variant='outlined' />
              )}
              {summary.unchanged > 0 && (
                <Chip label={`${summary.unchanged} already ${summary.unchanged === 1 ? 'exists' : 'exist'}`} size='small' variant='outlined' />
              )}
              {summary.invalid > 0 && (
                <Chip label={`${summary.invalid} to fix`} size='small' color='error' variant='outlined' />
              )}
            </Stack>

            <Typography variant='caption' color='text.secondary'>
              Read from the "{preview.sheetName}" sheet, with column headings on row {preview.headerRowNumber}.
            </Typography>

            {preview.unmatchedHeaders.length > 0 && (
              <Alert severity='info'>
                These columns were ignored because they are not part of a product category: {preview.unmatchedHeaders.join(', ')}.
              </Alert>
            )}

            {summary.invalid > 0 && (
              <Alert severity='warning'>
                {summary.invalid} {summary.invalid === 1 ? 'row needs' : 'rows need'} attention and will not be saved.
                Fix {summary.invalid === 1 ? 'it' : 'them'} in your file and upload it again{summary.valid > 0
                  ? `, or continue to save the ${summary.valid === 1 ? '1 row' : `${summary.valid} rows`} that are ready.`
                  : '.'}
              </Alert>
            )}

            {summary.toUpdate > 0 && (
              <Alert severity='info'>
                {summary.toUpdate} {summary.toUpdate === 1 ? 'category already exists and will have its reference(s) replaced' : 'categories already exist and will have their reference(s) replaced'} with the value in your file.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 380, overflowX: 'auto' }}>
              <Table size='small' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Subcategory One</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Subcategory Two</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference(s)</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map(row => (
                    <TableRow key={row.rowNumber} sx={!row.isValid ? { backgroundColor: theme => alpha(theme.palette.error.main, 0.06) } : undefined}>
                      <TableCell sx={{ color: 'text.secondary' }}>{row.rowNumber}</TableCell>
                      <TableCell>{row.category || '–'}</TableCell>
                      <TableCell>{row.subCategoryOne || '–'}</TableCell>
                      <TableCell>{row.subCategoryTwo || '–'}</TableCell>
                      <TableCell>
                        {row.isValid ? (
                          <Stack spacing={0.25}>
                            <Typography variant='body2'>{referenceLabel(row.goodCategoryReference) || '–'}</Typography>
                            {row.action === 'update' && (
                              <Typography variant='caption' color='text.secondary'>
                                Currently: {referenceLabel(row.existingReference) || 'none'}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Stack spacing={0.25}>
                            {row.errors.map((error, index) => (
                              <Typography key={index} variant='caption' color='error.main'>{error}</Typography>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.isValid
                          ? <Chip label={changeLabel(row)} size='small' variant='outlined' />
                          : <Typography variant='caption' color='text.secondary'>Skipped</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}

        {!isParsing && !preview && !fileErrorMessage && (
          <Typography variant='body2' color='text.secondary'>
            Choose a file to see what will be added or updated before anything is saved. Your file needs a
            Category column; Subcategory One, Subcategory Two and Reference(s) are optional. Download the
            template for the exact layout.
          </Typography>
        )}
      </Stack>
    </DialogWrapper>
  )
}
